import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  templatesApi,
  type LibraryTemplateSummary,
  type TemplateDirection,
  type TemplateWritePayload,
} from "./api";

const KEY = ["templates"] as const;

export function useTemplates() {
  return useQuery({
    queryKey: [...KEY, "list"],
    queryFn: templatesApi.list,
    staleTime: 30_000,
  });
}

export function useTemplate(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, "detail", id],
    queryFn: () => templatesApi.get(id!),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TemplateWritePayload) => templatesApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...KEY, "list"] }),
  });
}

export function useUpdateTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TemplateWritePayload) => templatesApi.update(id, data),
    onSuccess: (updated) => {
      qc.setQueryData([...KEY, "detail", id], updated);
      qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => templatesApi.remove(id),
    onSuccess: (_d, id) => {
      qc.setQueryData<LibraryTemplateSummary[]>([...KEY, "list"], (old) =>
        old?.filter((t) => t.id !== id),
      );
      qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    },
  });
}

export function useImportTemplate() {
  return useMutation({
    mutationFn: (file: File) => templatesApi.importFile(file),
  });
}

export function useGenerateTemplate() {
  return useMutation({
    mutationFn: ({ brief, direction }: { brief: string; direction: TemplateDirection }) =>
      templatesApi.generate(brief, direction),
  });
}
